import type React from "react";
import { useCallback } from "react";

import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Modal } from "@/components/ui/modal";
import { Note } from "@/components/ui/note";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";

export function GenerateAllDialog({
  children,
  accessionIds,
  onQueued,
}: {
  children: React.ReactNode;
  accessionIds: number[];
  /**
   * Called with the just-queued accession IDs after the mutation
   * succeeds, so the parent can flip them into its local "Generating"
   * state. Realtime invalidation handles the eventual prune.
   */
  onQueued?: (ids: number[]) => void;
}) {
  const utils = trpc.useUtils();
  const { mutate, data, isPending, isSuccess } =
    trpc.reports.generateAll.useMutation(
      mutationResponse({
        successMessage: (res) => `${res?.queued} reports queued for generation`,
        errorTitle: "Failed to queue reports",
        onSuccess(_res, vars) {
          // Belt-and-suspenders: if SSE is down we still want the list
          // to refresh as workers complete. Realtime is the primary
          // signal.
          utils.accession.requiringReports.invalidate();
          onQueued?.(vars.accessionIds);
        },
      }),
    );

  const onConfirm = useCallback(() => {
    mutate({ accessionIds });
  }, [accessionIds, mutate]);

  return (
    <DialogTrigger>
      {children}
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Generate reports</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {isSuccess ? (
              <Note intent="info">
                {data?.queued} reports have been queued for generation. They
                will be processed in the background.
              </Note>
            ) : (
              <Text elementType="p">
                Generate reports for {accessionIds.length} accessions?
              </Text>
            )}
          </DialogBody>
          <DialogFooter>
            {isSuccess ? (
              <DialogCloseButton>Close</DialogCloseButton>
            ) : (
              <>
                <Button onPress={onConfirm} isPending={isPending}>
                  Generate
                </Button>
                <DialogCloseButton isDisabled={isPending}>
                  Cancel
                </DialogCloseButton>
              </>
            )}
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
